// components/ora/Chat/Companion/chatSpeech.ts
//
// The browser's speech synthesis, behind one module (ORA-464, "Read aloud").
//
// Two reasons it is not called directly from the component:
//
// - **Only one answer may be read at a time.** `speechSynthesis` is a single
//   global queue, so starting a second answer has to stop the first — and the
//   button on the first has to stop looking like it is speaking. That is a
//   handshake between two components, which means it belongs to neither.
// - **The `end` and `error` events are not reliable** across browsers when an
//   utterance is cancelled mid-sentence. The callback here is invoked by this
//   module on cancellation, so "stopped speaking" is a fact of our code rather
//   than a promise the platform sometimes keeps.

/** True when this browser can speak at all; the button is hidden when it can't. */
export const isSpeechSynthesisAvailable = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.speechSynthesis !== 'undefined' &&
  window.speechSynthesis !== null &&
  typeof window.SpeechSynthesisUtterance === 'function';

/** The `onDone` of whatever is speaking now, so it can be told when it stops. */
let currentOnDone: (() => void) | null = null;

/** Runs an `onDone` once, and only while it is still the current one. */
const settle = (onDone: () => void) => {
  if (currentOnDone !== onDone) return;
  currentOnDone = null;
  onDone();
};

/**
 * Stop whatever is being read. Safe to call when nothing is: the queue is
 * cleared either way, which is what makes this usable from an unmount effect.
 */
export const stopSpeaking = (): void => {
  const pending = currentOnDone;
  currentOnDone = null;

  if (isSpeechSynthesisAvailable()) window.speechSynthesis.cancel();
  // The previous speaker is told directly rather than through `cancel`'s
  // events, which browsers deliver inconsistently.
  pending?.();
};

/**
 * Read `text` aloud, stopping anything already being read. `onDone` fires once
 * — when the utterance finishes, fails, or is cancelled by a later call.
 *
 * Returns false when this browser cannot speak, so the caller never shows a
 * speaking state that will not end.
 */
export const speak = (text: string, onDone: () => void): boolean => {
  if (!isSpeechSynthesisAvailable() || text.trim() === '') return false;

  stopSpeaking();

  const utterance = new window.SpeechSynthesisUtterance(text);
  currentOnDone = onDone;
  utterance.onend = () => settle(onDone);
  utterance.onerror = () => settle(onDone);

  window.speechSynthesis.speak(utterance);
  return true;
};
